import type { FC } from 'react';
import { Button, Code, Group, List, Stack, Text, Tooltip } from '@mantine/core';
import { VISIBLE_DIRTY_FILES_COUNT } from './FilesTab.constants';

interface UnsavedVersionSwitchModalProps {
    dirtyFilePaths: string[];
    onDiscard: () => void;
    onKeepEditing: () => void;
    onSave: () => void;
}

const getDisplayName = (path: string): string => path.split('/').pop() ?? path;

const UnsavedVersionSwitchModal: FC<UnsavedVersionSwitchModalProps> = ({
    dirtyFilePaths,
    onDiscard,
    onKeepEditing,
    onSave
}) => {
    const visibleDirtyFilePaths = dirtyFilePaths.slice(
        0,
        VISIBLE_DIRTY_FILES_COUNT
    );
    const remainingDirtyFilePaths = dirtyFilePaths.slice(
        VISIBLE_DIRTY_FILES_COUNT
    );

    return (
        <Stack gap="md">
            <Text size="sm">You have unsaved changes in:</Text>
            <List size="sm">
                {visibleDirtyFilePaths.map((path) => (
                    <List.Item key={path}>
                        <Code>{getDisplayName(path)}</Code>
                    </List.Item>
                ))}
            </List>
            {remainingDirtyFilePaths.length > 0 ? (
                <Tooltip
                    multiline
                    label={
                        <Stack gap={2}>
                            {remainingDirtyFilePaths.map((path) => (
                                <Text key={path} size="xs">
                                    {getDisplayName(path)}
                                </Text>
                            ))}
                        </Stack>
                    }
                >
                    <Text
                        className="w-fit cursor-help"
                        component="span"
                        size="sm"
                    >
                        +{remainingDirtyFilePaths.length} more
                    </Text>
                </Tooltip>
            ) : undefined}
            <Text size="sm">What would you like to do?</Text>
            <Group gap="sm" justify="flex-end">
                <Button variant="default" onClick={onKeepEditing}>
                    Keep Editing
                </Button>
                <Button color="red" variant="outline" onClick={onDiscard}>
                    Discard
                </Button>
                <Button onClick={onSave}>Save</Button>
            </Group>
        </Stack>
    );
};

export default UnsavedVersionSwitchModal;
